import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePlayer } from '../contexts/PlayerContext';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { logGameComplete } from '../lib/firebase';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';

interface SessionPlayerScore {
  playerId: string;
  nickname: string;
  gameScore: number;
  sessionScore: number;
  totalScore: number;
  rank: number;
}

interface PlayerScore {
  playerId?: string; // Optional - only included for admin users
  nickname: string;
  score: number;
  rank: number;
}

function Leaderboard() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  
  const { player, isAuthenticated, updateSessionScore } = usePlayer();
  const { scores, players, resetGameState } = useSocket();
  const { user } = useAuth();
  const lastAddedScore = useRef<{ roomId: string; playerId: string; score: number } | null>(null);
  const lastRoomId = useRef<string | null>(null);
  
  // State for fetched historical scores
  const [fetchedScores, setFetchedScores] = useState<PlayerScore[]>([]);
  const [isLoadingScores, setIsLoadingScores] = useState(false);
  const [fetchError, setFetchError] = useState('');

  // Use socket scores if available, otherwise use fetched scores
  const displayScores = scores.length > 0 ? scores : fetchedScores;

  // Fetch historical scores when socket scores are empty (past games)
  useEffect(() => {
    const fetchHistoricalScores = async () => {
      // Only fetch if socket scores are empty and we have a roomId and not already loading/loaded
      if (scores.length === 0 && roomId && !isLoadingScores && fetchedScores.length === 0) {
        console.log(`📊 Socket scores empty, fetching historical scores for room ${roomId}`);
        setIsLoadingScores(true);
        setFetchError('');
        
        try {
          const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
          // Include requesting user email for admin check
          const queryParams = new URLSearchParams();
          if (user?.email) {
            queryParams.append('requestingUserEmail', user.email);
          }
          const response = await fetch(`${serverUrl}/api/sessions/${roomId}/leaderboard?${queryParams}`);
          
          if (response.ok) {
            const data = await response.json();
            console.log(`📊 Fetched historical leaderboard data:`, data);
            
            // Transform Firebase leaderboard to match socket score format
            const transformedScores = data.leaderboard.map((entry: any) => ({
              playerId: entry.playerId,
              nickname: entry.nickname,
              score: entry.totalScore,
              rank: entry.rank
            }));
            
            console.log(`📊 Transformed ${transformedScores.length} historical scores`);
            setFetchedScores(transformedScores);
          } else {
            console.warn(`⚠️ Failed to fetch historical scores: ${response.status}`);
            setFetchError('Failed to load historical scores');
          }
        } catch (err) {
          console.error('❌ Error fetching historical scores:', err);
          setFetchError('Failed to load scores');
        } finally {
          setIsLoadingScores(false);
        }
      }
    };
    
    fetchHistoricalScores();
  }, [scores.length, roomId, fetchedScores.length]);

  // Calculate session-enhanced scores for display
  const sessionScores: SessionPlayerScore[] = displayScores.map(score => {
    const isCurrentPlayer = score.playerId === player?.id;
    const sessionScore = isCurrentPlayer ? (player.sessionScore - score.score) : 0; // Previous session score
    const totalScore = sessionScore + score.score; // Total session score for current player
    
    console.log('📊 SessionScore calc for', score.nickname, ':', {
      playerId: score.playerId,
      isCurrentPlayer,
      gameScore: score.score,
      playerSessionScore: player?.sessionScore,
      calculatedSessionScore: sessionScore,
      totalScore: totalScore
    });
    
    return {
      playerId: score.playerId,
      nickname: score.nickname,
      gameScore: score.score,
      sessionScore: sessionScore,
      totalScore: totalScore,
      rank: score.rank // Keep original game-based ranking
    };
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Track game completion when leaderboard loads - only update session score once
  useEffect(() => {
    console.log('📊 Leaderboard useEffect triggered. Scores length:', displayScores.length, 'Player:', player?.id, 'RoomId:', roomId);
    
    if (displayScores.length > 0 && player && roomId) {
      const playerScore = displayScores.find(s => s.playerId === player.id);
      if (playerScore) {
        // Check if we've already added this exact score for this player in this room
        const hasAlreadyAdded = lastAddedScore.current && 
          lastAddedScore.current.roomId === roomId &&
          lastAddedScore.current.playerId === player.id &&
          lastAddedScore.current.score === playerScore.score;

        console.log('📊 Score check - hasAlreadyAdded:', hasAlreadyAdded, 'playerScore.score:', playerScore.score);

        if (!hasAlreadyAdded) {
          console.log('📊 Leaderboard: Adding game score to session:', playerScore.score);
          console.log('📊 Current session score before update:', player.sessionScore);
          console.log('📊 Player object ID:', player.id);
          console.log('📊 Last added score ref:', lastAddedScore.current);
          
          logGameComplete(roomId, playerScore.score, playerScore.rank);
          // Update session score with the game points earned - only once per game
          updateSessionScore(playerScore.score);
          
          // Track this specific addition to prevent duplicates
          lastAddedScore.current = {
            roomId,
            playerId: player.id,
            score: playerScore.score
          };
          
          console.log('📊 Updated last added score ref:', lastAddedScore.current);
        } else {
          console.log('📊 Skipping duplicate score addition. Already added:', lastAddedScore.current);
        }
      } else {
        console.log('📊 No player score found for player:', player.id);
      }
    } else {
      console.log('📊 Conditions not met - displayScores.length:', displayScores.length, 'player:', !!player, 'roomId:', roomId);
    }
  }, [displayScores, player, roomId]); // Removed updateSessionScore from dependencies to prevent multiple calls

  // Reset the score tracking when room actually changes (not from null to first room)
  useEffect(() => {
    if (roomId && lastRoomId.current !== null && lastRoomId.current !== roomId) {
      console.log('📊 Room actually changed from', lastRoomId.current, 'to', roomId);
      lastAddedScore.current = null;
      lastRoomId.current = roomId;
      console.log('📊 Reset score tracking for room change');
    } else if (roomId && lastRoomId.current === null) {
      // First time setting the room, just track it without resetting
      lastRoomId.current = roomId;
      console.log('📊 Initial room set to:', roomId);
    }
  }, [roomId]);

  const handleReturnToLobby = () => {
    resetGameState();
    navigate('/lobby');
  };

  const getRankEmoji = (rank: number) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '🏅';
    }
  };

  const getRankClass = (rank: number, isCurrentPlayer: boolean) => {
    if (isCurrentPlayer) {
      return 'bg-primary/10 border-primary text-primary';
    }
    
    switch (rank) {
      case 1: return 'bg-yellow-50 border-yellow-300 text-yellow-900';
      case 2: return 'bg-muted border-border text-foreground';
      case 3: return 'bg-orange-50 border-orange-300 text-orange-900';
      default: return 'bg-muted border-border text-muted-foreground';
    }
  };

  // Show loading state when fetching historical scores
  if (isLoadingScores) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading scores...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2">🏆 Game Results</h1>
          <p className="text-muted-foreground text-lg">
            Great job everyone! Here's how you ranked in this game:
          </p>
          {player && player.sessionScore > 0 && (
            <p className="text-primary text-sm mt-2">
              Your session total: {player.sessionScore.toLocaleString()} points
            </p>
          )}
        </div>

        {/* Leaderboard */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-center">Game Results</CardTitle>
            <CardDescription className="text-center">Ranked by current game performance (session totals shown for you)</CardDescription>
          </CardHeader>
          <CardContent>
            {displayScores.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-muted-foreground mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">No scores available</h3>
                <p className="text-muted-foreground">{fetchError || 'The game may not have finished properly.'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sessionScores.map((score) => {
                  const isCurrentPlayer = score.playerId === player?.id;
                  return (
                    <div
                      key={score.playerId}
                      className={`p-4 rounded-lg border-2 transition-all ${getRankClass(score.rank, isCurrentPlayer)}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="text-2xl">
                            {getRankEmoji(score.rank)}
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="font-bold text-lg">{score.nickname}</span>
                              {isCurrentPlayer && (
                                <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded">
                                  YOU
                                </span>
                              )}
                            </div>
                            {user?.email === 'caleb.brown@x-team.com' && score.playerId && (
                              <div className="text-xs text-muted-foreground">
                                {score.playerId}
                              </div>
                            )}
                            <div className="text-sm opacity-75">
                              Rank #{score.rank}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold">
                            {(score.gameScore ?? 0).toLocaleString()}
                          </div>
                          <div className="text-sm opacity-75">
                            {score.sessionScore > 0 && isCurrentPlayer ? `Session: ${(score.totalScore ?? 0).toLocaleString()}` : 'game points'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats Summary */}
        {sessionScores.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>🎯 Game Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{players.length || displayScores.length}</div>
                  <div className="text-sm text-muted-foreground">Players</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">
                    {sessionScores.length > 0 && sessionScores.filter(s => s.gameScore != null).length > 0
                      ? Math.max(...sessionScores.filter(s => s.gameScore != null).map(s => s.gameScore)).toLocaleString()
                      : '0'}
                  </div>
                  <div className="text-sm text-muted-foreground">Top Game Score</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">
                    {player && player.sessionScore > 0 ? 
                      player.sessionScore.toLocaleString() : 
                      sessionScores.filter(s => s.gameScore != null).length > 0
                        ? Math.round(sessionScores.filter(s => s.gameScore != null).reduce((sum, s) => sum + (s.gameScore ?? 0), 0) / sessionScores.filter(s => s.gameScore != null).length).toLocaleString()
                        : '0'
                    }
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {player && player.sessionScore > 0 ? 'Your Session Total' : 'Avg Game Score'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">
                    {player ? sessionScores.find(s => s.playerId === player.id)?.rank || '-' : '-'}
                  </div>
                  <div className="text-sm text-muted-foreground">Your Game Rank</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center mb-8">
          <Button
            onClick={handleReturnToLobby}
            size="lg"
            className="px-8"
          >
            🏠 Return to the Lobby
          </Button>
        </div>

        {/* Share Results (Future Feature) */}
        <div className="text-center">
          <p className="text-muted-foreground text-sm">
            Thanks for playing Xperience Trivia! 🎉
          </p>
          {/* TODO: Add social sharing buttons */}
        </div>
      </div>
    </div>
  );
}

export default Leaderboard; 